import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Code } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Technology {
  id: string;
  name: string;
  category: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

const TECH_CATEGORIES = [
  'Frontend',
  'Backend',
  'Database',
  'Cloud',
  'DevOps',
  'Language',
  'Framework',
  'API',
  'Cache',
  'Mobile',
  'Testing'
];

export default function TechnologyStackManager() {
  const [technologies, setTechnologies] = useState<Technology[]>([]);
  const [editingTech, setEditingTech] = useState<Technology | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Frontend',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchTechnologies();
  }, []);

  const fetchTechnologies = async () => {
    const { data, error } = await supabase
      .from('technologies')
      .select('*')
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching technologies:', error);
    } else {
      setTechnologies(data || []);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingTech) {
      const { error } = await supabase
        .from('technologies')
        .update(formData)
        .eq('id', editingTech.id);

      if (error) {
        console.error('Error updating technology:', error);
      } else {
        fetchTechnologies();
        resetForm();
      }
    } else {
      const { error } = await supabase
        .from('technologies')
        .insert([formData]);

      if (error) {
        console.error('Error creating technology:', error);
      } else {
        fetchTechnologies();
        resetForm();
      }
    }
  };

  const handleEdit = (tech: Technology) => {
    setEditingTech(tech);
    setFormData({
      name: tech.name,
      category: tech.category,
      display_order: tech.display_order,
      is_active: tech.is_active
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this technology?')) {
      const { error } = await supabase
        .from('technologies')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting technology:', error);
      } else {
        fetchTechnologies();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'Frontend',
      display_order: 0,
      is_active: true
    });
    setEditingTech(null);
  };

  const groupedTechnologies = technologies.reduce((acc, tech) => {
    if (!acc[tech.category]) {
      acc[tech.category] = [];
    }
    acc[tech.category].push(tech);
    return acc;
  }, {} as Record<string, Technology[]>);

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Technology Stack Manager</h1>
        <p className="text-gray-600">Manage technologies displayed on the homepage</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingTech ? 'Edit Technology' : 'Add New Technology'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Technology Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="React, Node.js, AWS..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {TECH_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                  Active
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {editingTech ? 'Update' : 'Create'}
                </button>
                {editingTech && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Technologies ({technologies.length})</h2>
            </div>
            <div className="p-6 space-y-6">
              {Object.entries(groupedTechnologies).map(([category, techs]) => (
                <div key={category}>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Code className="h-5 w-5 text-blue-600" />
                    {category} ({techs.length})
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {techs.map((tech) => (
                      <div
                        key={tech.id}
                        className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 mb-1">{tech.name}</h4>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">Order: {tech.display_order}</span>
                              {!tech.is_active && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <button
                              onClick={() => handleEdit(tech)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(tech.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {technologies.length === 0 && (
                <div className="p-12 text-center text-gray-500">
                  No technologies found. Add your first technology to get started.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
